import { Dispatch, SetStateAction, useState } from 'react';
import BulkAddToGroupDialog from './groups/bulk-add-to-group';
import BulkRemoveFromGroupDialog from './groups/bulk-remove-from-group';
import RestoreUsersDialog from './users/restore-users-dialog';
import SuspendUsersDialog from './users/suspend-users-dialog';
import SignOutUsersDialog from './users/signout-users-dialog';
import PasswordResetDialog from './users/password-reset-dialog';
import SetOrgsDialog from './users/set-orgs-dialog';
import ShareContactDialog from './users/share-contact-dialog';
import ChangeRoleDialog from './users/change-role-dialog';
import DeleteUsersDialog from './users/delete-users-dialog';
import ImapSettingsDialog from './gmail/imap-settings';
import BulkAddGmailLabelDialog from './gmail/bulk-add-gmail-label';
import PopSettingsDialog from './gmail/pop-settings';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

interface UserGridActionsProps {
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  userRoles: string[];
  changeLogRoles: string[];
}

export default function UserGridActions({
  selectedRows,
  setSelectedRows,
  userRoles,
  changeLogRoles,
}: UserGridActionsProps) {
  const [actionsTab, setActionsTab] = useState('users');
  const [genericState, setGenericState] = useState<
    | 'bulkAddUsersToGroup'
    | 'bulkRemoveUsersFromGroup'
    | 'setOrgs'
    | 'suspendUsers'
    | 'restoreUsers'
    | 'signOutUsers'
    | 'forcePasswordReset'
    | 'contactSharing'
    | 'changeGpanelRole'
    | 'deleteUsers'
    | 'imapSettings'
    | 'popSettings'
    | 'bulkAddGmailLabel'
    | ''
  >('');

  function GridTab({
    children,
    value,
  }: {
    children: React.ReactNode;
    value: string;
  }) {
    return (
      <Button
        onMouseOver={() => {
          setActionsTab(value);
        }}
        color={actionsTab === value ? 'primary' : 'inherit'}
      >
        {children}
      </Button>
    );
  }

  function GridButton({
    children,
    disabled,
    onClick,
  }: {
    children: React.ReactNode;
    onClick: () => void;
    disabled: boolean;
  }) {
    return (
      <Grid size={{ xs: 6 }}>
        <Button
          size="small"
          sx={{ textTransform: 'none' }}
          color="inherit"
          disabled={disabled}
          onClick={() => {
            onClick();
          }}
        >
          {children}
        </Button>
      </Grid>
    );
  }

  return (
    <Stack flexDirection="row" alignItems="flex-start" sx={{ width: 600 }}>
      <Stack>
        <GridTab value="users">Users</GridTab>
        <GridTab value="groups">Groups</GridTab>
        <GridTab value="gmail">Gmail</GridTab>
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Grid container sx={{ ml: 2, maxHeight: 140, overflowY: 'auto' }}>
        {actionsTab === 'groups' ? (
          <>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkAddUsersToGroup.value
                )
              }
              onClick={() => setGenericState('bulkAddUsersToGroup')}
            >
              Add To Group
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkRemoveUsersFromGroup.value
                )
              }
              onClick={() => setGenericState('bulkRemoveUsersFromGroup')}
            >
              Remove From Group
            </GridButton>
            <Grid size={{ xs: 6 }}>
              <Box width={100}></Box>
            </Grid>
          </>
        ) : null}
        {actionsTab === 'users' ? (
          <>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.EditUserOrgUnit.value
                ) ||
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkSetOrganization.value
                )
              }
              onClick={() => setGenericState('setOrgs')}
            >
              Set Organization
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkSuspendUsers.value
                )
              }
              onClick={() => setGenericState('suspendUsers')}
            >
              Suspend Users
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkRestoreUsers.value
                )
              }
              onClick={() => setGenericState('restoreUsers')}
            >
              Restore Users
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkChangeGpanelRole.value
                )
              }
              onClick={() => setGenericState('changeGpanelRole')}
            >
              Change gPanel Role
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkForcePasswordReset.value
                )
              }
              onClick={() => setGenericState('forcePasswordReset')}
            >
              Force Password Reset
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkSetContactSharing.value
                )
              }
              onClick={() => setGenericState('contactSharing')}
            >
              Share Contact With Directory
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkDeleteUsers.value
                )
              }
              onClick={() => setGenericState('deleteUsers')}
            >
              Delete Users
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkSignOutUsers.value
                )
              }
              onClick={() => setGenericState('signOutUsers')}
            >
              Sign Out Users
            </GridButton>
          </>
        ) : null}
        {actionsTab === 'gmail' ? (
          <>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.BulkAddGmailLabel.value
                )
              }
              onClick={() => setGenericState('bulkAddGmailLabel')}
            >
              Add Label
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(userRoles, UserRoles.BulkSetImap.value)
              }
              onClick={() => setGenericState('imapSettings')}
            >
              Enable/Disable IMAP
            </GridButton>
            <GridButton
              disabled={
                !isAbleToSeeUIElement(userRoles, UserRoles.BulkSetPop.value)
              }
              onClick={() => setGenericState('popSettings')}
            >
              Enable/Disable POP
            </GridButton>
          </>
        ) : null}
      </Grid>
      <BulkAddToGroupDialog
        open={genericState === 'bulkAddUsersToGroup'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <BulkRemoveFromGroupDialog
        open={genericState === 'bulkRemoveUsersFromGroup'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <RestoreUsersDialog
        open={genericState === 'restoreUsers'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <SuspendUsersDialog
        open={genericState === 'suspendUsers'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <SignOutUsersDialog
        open={genericState === 'signOutUsers'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <PasswordResetDialog
        open={genericState === 'forcePasswordReset'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <SetOrgsDialog
        open={genericState === 'setOrgs'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        userRoles={userRoles}
        changeLogRoles={changeLogRoles}
      />
      <ShareContactDialog
        open={genericState === 'contactSharing'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <ChangeRoleDialog
        open={genericState === 'changeGpanelRole'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <DeleteUsersDialog
        open={genericState === 'deleteUsers'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <BulkAddGmailLabelDialog
        open={genericState === 'bulkAddGmailLabel'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <ImapSettingsDialog
        open={genericState === 'imapSettings'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
      <PopSettingsDialog
        open={genericState === 'popSettings'}
        handleClose={() => setGenericState('')}
        selectedUsers={selectedRows}
        setSelectedUsers={setSelectedRows}
        changeLogRoles={changeLogRoles}
      />
    </Stack>
  );
}
